import { Page, Text, View, Document } from '@react-pdf/renderer';
import { reportStyles } from './styles/ReportStyles';
import renderEmergencyMessageContent from '../components/RenderEmergencyMessageContent';
import { StringKeys } from '../../resources/strings/StringKeys';
import { FooterText } from '../components/Footer';
import { SafeguardLogo } from '../components/SafeguardLogo';
import type { EmergencyDataMap } from '@/views/UserViews/profile/Profile';

type TFunction = (key: string, options?: Record<string, unknown>) => string;

interface EmergencyContactsScope {
  unavailable: boolean;
  responders: { first_name: string; last_name: string }[];
  emailContacts: { name: string; email: string }[];
}

interface UserData {
  first_name: string;
  last_name: string;
  auth_id: string;
  org_code: string;
  phone_number: string;
  role: string;
}

interface EmergencyContacts {
  group: EmergencyContactsScope & { groupName: string };
  company: EmergencyContactsScope;
}

const renderEmergencyContactsScope = (
  title: string,
  scope: EmergencyContactsScope | null | undefined,
  t: TFunction,
) => {
  const safe = scope ?? {
    unavailable: false,
    responders: [],
    emailContacts: [],
  };

  return (
    <View style={reportStyles.section}>
      <Text style={reportStyles.boldText}>{title}</Text>

      {safe.unavailable ? (
        <Text style={reportStyles.text}>
          {t(StringKeys.UNAVAILABLE_TRY_DOWNLOADING_THE_PDF_AGAIN)}
        </Text>
      ) : (
        <View>
          <View style={reportStyles.section}>
            <Text style={reportStyles.boldText}>{t(StringKeys.RESPONDERS)}:</Text>
            {safe.responders.length === 0 ? (
              <Text style={reportStyles.text}>{t(StringKeys.NONE)}</Text>
            ) : (
              safe.responders.map((r, i) => (
                <Text key={`r-${i}`} style={reportStyles.text}>
                  {`${r.first_name} ${r.last_name}`.trim()}
                </Text>
              ))
            )}
          </View>
          <View style={reportStyles.section}>
            <Text style={reportStyles.boldText}>{t(StringKeys.EMAILS)}:</Text>
            {safe.emailContacts.length === 0 ? (
              <Text style={reportStyles.text}>{t(StringKeys.NONE)}</Text>
            ) : (
              safe.emailContacts.map((c, i) => (
                <Text key={`e-${i}`} style={reportStyles.text}>
                  {`${c.name} — ${c.email}`}
                </Text>
              ))
            )}
          </View>
        </View>
      )}
    </View>
  );
};

export const UserExportedData = (
  userData: UserData,
  emergencyData: EmergencyDataMap,
  emergencyContacts: EmergencyContacts,
  t: TFunction,
) => (
  <Document>
    <Page size="A4" style={reportStyles.page}>
      <SafeguardLogo />
      {/* User Information Section */}
      <View style={reportStyles.section}>
        <Text style={reportStyles.header}>
          {t(StringKeys.PERSONAL_INFORMATION)}
        </Text>
        <View style={reportStyles.section}>
          <Text style={reportStyles.text}>
            {t(StringKeys.NAME)}:{' '}
            {`${userData.first_name} ${userData.last_name}`}
          </Text>
        </View>
        <View style={reportStyles.section}>
          <Text style={reportStyles.text}>
            {t(StringKeys.ORGANIZATION_CODE)}: {userData.org_code}
          </Text>
        </View>
        <View style={reportStyles.section}>
          <Text style={reportStyles.text}>
            {t(StringKeys.PHONE_NUMBER)}: {userData.phone_number}
          </Text>
        </View>
        <View style={reportStyles.section}>
          <Text style={reportStyles.text}>
            {t(StringKeys.ROLE)}: {userData.role}
          </Text>{' '}
        </View>
      </View>
      <View style={reportStyles.row}></View>
      {/* New Emergency Contacts Section */}
      <View style={reportStyles.section}>
        <Text style={reportStyles.header}>
          {t(StringKeys.EMERGENCY_CONTACTS)}
        </Text>

        {renderEmergencyContactsScope(
          emergencyContacts?.group?.groupName
            ? t(StringKeys.GROUP_EMERGENCY_RESPONDERS, { groupName: emergencyContacts.group.groupName })
            : t(StringKeys.GROUP_EMERGENCY_RESPONDERS_FALLBACK),
          emergencyContacts?.group,
          t,
        )}

        {renderEmergencyContactsScope(
          t(StringKeys.COMPANY_WIDE_EMERGENCY_RESPONDERS),
          emergencyContacts?.company,
          t,
        )}
      </View>

      <View style={reportStyles.row}></View>
      {/* Emergency History Section */}
      <View style={reportStyles.section}>
        <Text style={reportStyles.header}>
          {t(StringKeys.EMERGENCY_HISTORY)}
        </Text>

        {Object.entries(emergencyData).length > 0 ? (
          Object.entries(emergencyData).map(
            ([emergencyId, emergency], index) => (
              <View key={emergencyId} style={reportStyles.section}>
                <View style={reportStyles.subsectionHeader}>
                  <Text style={reportStyles.text}>
                    {index + 1}. {emergency?.emergencyDetails?.incident} -{' '}
                    {new Date(
                      emergency?.emergencyDetails?.creation_timestamp ?? 0,
                    ).toLocaleString()}
                  </Text>
                </View>

                {emergency.emergencyDetails && (
                  <>
                    <View style={reportStyles.section}>
                      <Text style={reportStyles.text}>
                        {t(StringKeys.INITIATED_BY)}:{' '}
                        {emergency.emergencyDetails.creator_name}
                      </Text>
                    </View>
                    <View style={reportStyles.section}>
                      <Text style={reportStyles.text}>
                        {t(StringKeys.RESOLVED)}:{' '}
                        {emergency.emergencyDetails.resolved_timestamp
                          ? t(StringKeys.YES)
                          : t(StringKeys.NO)}
                      </Text>
                    </View>
                  </>
                )}
                <View style={reportStyles.row}></View>
                <View style={reportStyles.section}>
                  <Text style={reportStyles.boldText}>
                    {t(StringKeys.MESSAGES_SENT_BY_YOU)}:
                  </Text>
                  {emergency.userMessages &&
                  emergency.userMessages.length > 0 ? (
                    emergency.userMessages.map((message, index) => {
                      const messageWithSender = {
                        ...message,
                        sender_first_name: userData.first_name,
                      };
                      return (
                        <View key={index} style={reportStyles.section}>
                          <Text
                            style={reportStyles.text}
                          >{`${new Date(message.sent_timestamp ?? 0).toLocaleString()} - ${renderEmergencyMessageContent(messageWithSender, emergency.emergencyDetails?.creator_name, emergency.emergencyDetails?.emergency_numbers.medical, t)}`}</Text>
                          <Text
                            style={reportStyles.text}
                          >{`${t(StringKeys.ADDRESS)}: ${message.address ? message.address : t(StringKeys.NOT_CAPTURED)}`}</Text>
                        </View>
                      );
                    })
                  ) : (
                    <View>
                      <Text style={reportStyles.text}>
                        {t(StringKeys.NONE)}
                      </Text>
                    </View>
                  )}
                </View>
                <View style={reportStyles.row}></View>
                <View style={reportStyles.section}>
                  <Text style={reportStyles.boldText}>
                    {t(StringKeys.LOCATIONS_SHARED_BY_YOU)}:
                  </Text>
                  {emergency.userLocations &&
                  emergency.userLocations.length > 0 ? (
                    emergency.userLocations.map((location, index) => (
                      <View key={index} style={reportStyles.row}>
                        <View style={reportStyles.section}>
                          <Text
                            style={reportStyles.text}
                          >{`${t(StringKeys.TIME)}: ${new Date(location.creation_timestamp ?? 0).toLocaleString()}`}</Text>
                          <Text
                            style={reportStyles.text}
                          >{`${t(StringKeys.COORDINATES)}: ${t(StringKeys.LATITUDE)}: ${location.latitude}, ${t(StringKeys.LONGITUDE)}: ${location.longitude}`}</Text>
                          <Text
                            style={reportStyles.text}
                          >{`${t(StringKeys.DEGREES_BEARING)}: ${location.degrees_bearing ?? t(StringKeys.NOT_CAPTURED)}`}</Text>
                          <Text
                            style={reportStyles.text}
                          >{`${t(StringKeys.SPEED)}: ${location.meters_per_second_speed != null ? `${location.meters_per_second_speed} ${t(StringKeys.METERS_PER_SECOND)}` : t(StringKeys.NOT_CAPTURED)}`}</Text>
                        </View>
                      </View>
                    ))
                  ) : (
                    <View>
                      <Text style={reportStyles.text}>
                        {t(StringKeys.NONE)}
                      </Text>
                      <View style={reportStyles.row}></View>
                    </View>
                  )}
                </View>
              </View>
            ),
          )
        ) : (
          <Text style={reportStyles.text}>{t(StringKeys.NONE)}</Text>
        )}
      </View>
      <FooterText user={userData} t={t} />
    </Page>
  </Document>
);
